import React, { useState } from 'react'
import { MultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import ExplainAuthenticatorDialog from './ExplainAuthenticatorDialog'
import { useTranslation } from 'react-i18next'

interface CodeStepProps {
    mfaResolver: MultiFactorResolver
    enrollmentId: string
}

const MFACodeStep: React.FC<CodeStepProps> = ({ mfaResolver, enrollmentId }) => {
    const { t } = useTranslation()
    const [code, setCode] = useState('')
    const [error, setError] = useState<string>('')
    const [explainOpen, setExplainOpen] = useState(false) // 1. Add state

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCode(e.target.value.replace(/\D/g, '')) // Ensure only numbers are entered
        if (error) setError('') // Clear error on input
    }

    const handleSubmit = async () => {
        try {
            const assertion = TotpMultiFactorGenerator.assertionForSignIn(enrollmentId, code)
            await mfaResolver.resolveSignIn(assertion)

            console.info('MFA successfully completed.')
        } catch (err: unknown) {
            if (err instanceof FirebaseError && err.code === 'auth/totp-challenge-timeout') {
                setError(t('login.mfa_step.error_expired'))
            } else {
                setError(t('login.mfa_step.error_incorrect'))
            }
            setCode('')
            console.warn('MFA code verification failed:', err)
        }
    }

    const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            await handleSubmit()
        }
    }

    return (
        <Box>
            <Stack>
                <Typography variant="h6">{t('login.mfa_step.title')}</Typography>
                <TextField
                    label={t('login.mfa_step.code_label')}
                    variant="outlined"
                    inputMode="numeric"
                    slotProps={{ htmlInput: { maxLength: 6 } }}
                    value={code}
                    onChange={handleChange}
                    onKeyDown={handleKeyPress}
                    fullWidth
                />

                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!code}>
                    {t('login.mfa_step.verify_button')}
                </Button>
                <Button variant="outlined" color="info" onClick={() => setExplainOpen(true)}>
                    {t('login.mfa_step.no_code_button')}
                </Button>

                {error && <Typography color="error">{error}</Typography>}
            </Stack>
            <ExplainAuthenticatorDialog open={explainOpen} onClose={() => setExplainOpen(false)} />
        </Box>
    )
}

export default MFACodeStep
